import { Box, Sheet, Stack, Typography } from "@mui/joy";
import { AxisBottom, AxisLeft, TicksRendererProps } from "@visx/axis";
import { localPoint } from "@visx/event";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { useParentSize } from "@visx/responsive";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import Color from "color";
import { index as d3Index, stack as d3Stack, scaleBand, ScaleBand, scaleLinear } from "d3";
import { ApiCircuit } from "griptonite-api-client";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { bestColor } from "@/utils/colorContrast";

import { Route } from "../../core/types";
import { useVenueCircuitsByUuid, useVenueRoutesByUuid } from "../../hooks/routes";
import { useCurrentVenueUuid } from "../../hooks/venues";
import formatNumber from "../../utils/formatNumber";
import { MapValue } from "../../utils/types";
import { RoutePinText } from "../Map/MapRoutePinSvg";
import { RouteCircle } from "../RouteCircle";
import { RouteName } from "../RoutesTable/RouteName";
import ColorStripesPattern from "./ColorStripesPattern";
import { BreakdownId, DimensionId, getBreakdownDimension } from "./dimensions";
import { MetricId, metrics } from "./metrics";
import { GraphData, GraphDataMap } from "./types";

type Props = {
  data: GraphData;
  metric: MetricId;
  axis: DimensionId;
  breakdown: BreakdownId | null;
  onColumnClick: (routeUuids: string[]) => void;
};

const RouteTick = ({ x, y, route }: { x: number; y: number; route?: Route }) => {
  const routeColor = Color(route?.colors?.split(":")?.[0] || "#FFFFFF");
  const pinDiameter = 16;
  return (
    <g transform={`translate(${x},${y})`}>
      <rect
        x={-(pinDiameter + 2) / 2}
        y={-(pinDiameter + 2) / 2}
        width={pinDiameter + 2}
        height={pinDiameter + 2}
        rx={route?.climbType === "SPORT" ? 3 : (pinDiameter + 2) / 2}
        fill={routeColor.toString()}
      />
      <rect
        x={-pinDiameter / 2}
        y={-pinDiameter / 2}
        width={pinDiameter}
        height={pinDiameter}
        rx={route?.climbType === "SPORT" ? 2 : pinDiameter / 2}
        stroke="black"
        strokeWidth="2"
        strokeOpacity="0.15"
        fill="transparent"
      />
      <text
        x={0}
        y={0}
        dy={1}
        fontSize={11}
        textAnchor="middle"
        dominantBaseline="middle"
        fontWeight="bold"
        fill={routeColor.isDark() ? "white" : "black"}
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
      >
        {route && Object.keys(route.displayRouteGroupMapping).length <= 1 ? (
          <RoutePinText route={route} />
        ) : (
          "?"
        )}
      </text>
    </g>
  );
};

const CircuitTick = ({ x, y, circuit }: { x: number; y: number; circuit?: ApiCircuit }) => {
  const textRef = useRef<SVGTextElement>(null);

  const allColors = circuit?.colors?.flatMap((c) => c.split(":")) || [];
  let foregroundColor = "black";
  let useStroke = false;
  if (allColors.length == 1) {
    foregroundColor = bestColor(allColors);
  } else {
    foregroundColor = "white";
    useStroke = true;
  }

  const [textBBox, setTextBBox] = useState<DOMRect | undefined>(undefined);

  useLayoutEffect(() => {
    if (textRef.current) {
      setTextBBox(textRef.current.getBBox());
    }
    return undefined;
  }, []);

  if (!circuit) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          ref={textRef}
          x={0}
          y={0}
          dy={1}
          fontSize={11}
          textAnchor="middle"
          dominantBaseline="middle"
          fontWeight="bold"
          // fill={circuitColor.isDark() ? "white" : "black"}
          fill="black"
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }}
        >
          No circuit
        </text>
      </g>
    );
  }

  return (
    <g transform={`translate(${x},${y})`}>
      {textBBox && (
        <>
          <rect
            fill={`url(#circuit-${circuit.uuid})`}
            width={textBBox.width + 8}
            height={textBBox.height + 3}
            x={-textBBox.width / 2 - 4}
            y={-textBBox.height / 2 - 1.5}
            rx={4}
          />
          <rect
            fill={"transparent"}
            stroke={"black"}
            strokeOpacity={0.2}
            strokeWidth={1}
            width={textBBox.width + 7}
            height={textBBox.height + 2}
            x={-textBBox.width / 2 - 3.5}
            y={-textBBox.height / 2 - 1}
            rx={3.5}
          />
        </>
      )}
      {useStroke ? (
        <text
          x={0}
          y={0}
          dy={1}
          fontSize={11}
          textAnchor="middle"
          dominantBaseline="middle"
          fontWeight="bold"
          fill={"black"}
          stroke={"black"}
          strokeWidth={2}
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }}
        >
          {circuit.name}
        </text>
      ) : null}
      <text
        ref={textRef}
        x={0}
        y={0}
        dy={1}
        fontSize={11}
        textAnchor="middle"
        dominantBaseline="middle"
        fontWeight="bold"
        // fill={circuitColor.isDark() ? "white" : "black"}
        fill={foregroundColor}
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
        }}
      >
        {circuit.name}
      </text>
    </g>
  );
};

const RouteTicks = ({ ticks, tickTransform }: TicksRendererProps<ScaleBand<string>>) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const routes = useVenueRoutesByUuid(currentVenueUuid);
  return ticks.map(({ value, index, to }) => {
    return (
      <g key={`visx-tick-${index}`} transform={tickTransform}>
        <RouteTick x={to.x} y={to.y + 10} route={routes?.[value]} />
      </g>
    );
  });
};

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 36 36">
    <path
      fill="#FFCC4D"
      d="M2.653 35C.811 35-.001 33.662.847 32.027L16.456 1.972c.849-1.635 2.238-1.635 3.087 0l15.609 30.056c.85 1.634.037 2.972-1.805 2.972H2.653z"
    />
    <path
      fill="#231F20"
      d="M15.583 28.953c0-1.333 1.085-2.418 2.419-2.418 1.333 0 2.418 1.085 2.418 2.418 0 1.334-1.086 2.419-2.418 2.419-1.334 0-2.419-1.085-2.419-2.419zm.186-18.293c0-1.302.961-2.108 2.232-2.108 1.241 0 2.233.837 2.233 2.108v11.938c0 1.271-.992 2.108-2.233 2.108-1.271 0-2.232-.807-2.232-2.108V10.66z"
    />
  </svg>
);

const DangerIcon = () => (
  <svg width="14" height="14" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5 7.5C5.14167 7.5 5.26042 7.45208 5.35625 7.35625C5.45208 7.26042 5.5 7.14167 5.5 7C5.5 6.85833 5.45208 6.73958 5.35625 6.64375C5.26042 6.54792 5.14167 6.5 5 6.5C4.85833 6.5 4.73958 6.54792 4.64375 6.64375C4.54792 6.73958 4.5 6.85833 4.5 7C4.5 7.14167 4.54792 7.26042 4.64375 7.35625C4.73958 7.45208 4.85833 7.5 5 7.5ZM4.5 5.5H5.5V2.5H4.5V5.5ZM5 10C4.30833 10 3.65833 9.86875 3.05 9.60625C2.44167 9.34375 1.9125 8.9875 1.4625 8.5375C1.0125 8.0875 0.65625 7.55833 0.39375 6.95C0.13125 6.34167 0 5.69167 0 5C0 4.30833 0.13125 3.65833 0.39375 3.05C0.65625 2.44167 1.0125 1.9125 1.4625 1.4625C1.9125 1.0125 2.44167 0.65625 3.05 0.39375C3.65833 0.13125 4.30833 0 5 0C5.69167 0 6.34167 0.13125 6.95 0.39375C7.55833 0.65625 8.0875 1.0125 8.5375 1.4625C8.9875 1.9125 9.34375 2.44167 9.60625 3.05C9.86875 3.65833 10 4.30833 10 5C10 5.69167 9.86875 6.34167 9.60625 6.95C9.34375 7.55833 8.9875 8.0875 8.5375 8.5375C8.0875 8.9875 7.55833 9.34375 6.95 9.60625C6.34167 9.86875 5.69167 10 5 10ZM5 9C6.11667 9 7.0625 8.6125 7.8375 7.8375C8.6125 7.0625 9 6.11667 9 5C9 3.88333 8.6125 2.9375 7.8375 2.1625C7.0625 1.3875 6.11667 1 5 1C3.88333 1 2.9375 1.3875 2.1625 2.1625C1.3875 2.9375 1 3.88333 1 5C1 6.11667 1.3875 7.0625 2.1625 7.8375C2.9375 8.6125 3.88333 9 5 9Z"
      fill="#FD5348"
    />
  </svg>
);

const GradeTick = ({
  x,
  y,
  value,
  targetStatus,
}: {
  x: number;
  y: number;
  value?: string;
  targetStatus?: "warning" | "danger";
}) => {
  const iconWidth = 14;
  const iconGap = 4;
  const textRef = useRef<SVGTextElement>(null);
  const [offset, setOffset] = useState(0);

  useLayoutEffect(() => {
    if (textRef.current) {
      const bbox = textRef.current.getBBox();
      const totalWidth = targetStatus ? iconWidth + iconGap + bbox.width : bbox.width;
      setOffset(-totalWidth / 2);
    }
  }, [value, targetStatus, iconWidth, iconGap]);

  return (
    <g transform={`translate(${x},${y})`}>
      <g transform={`translate(${offset}, 0)`}>
        {targetStatus === "warning" && (
          <g transform={`translate(0, 0)`}>
            <WarningIcon />
          </g>
        )}
        {targetStatus === "danger" && (
          <g transform={`translate(0, 0)`}>
            <DangerIcon />
          </g>
        )}

        <text
          ref={textRef}
          x={targetStatus ? iconWidth + iconGap : 0}
          y={0}
          dy={8}
          fontSize={13}
          textAnchor="start"
          dominantBaseline="middle"
          fontWeight="bold"
          fill={
            targetStatus === "warning" ? "#b36b00" : targetStatus === "danger" ? "#FD5348" : "black"
          }
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
          }}
        >
          {value}
        </text>
      </g>
    </g>
  );
};

const getGradeTicksComponent = (shouldAddTargets: boolean, axisData: GraphDataMap) => {
  if (!shouldAddTargets) {
    return undefined;
  }
  return ({ ticks, tickTransform }: TicksRendererProps<ScaleBand<string>>) => {
    return ticks.map(({ value, index, to, formattedValue }) => {
      const datum = axisData.get(value);
      return (
        <g key={`visx-tick-${index}`} transform={tickTransform}>
          <GradeTick x={to.x} y={to.y} value={formattedValue} targetStatus={datum?.targetStatus} />
        </g>
      );
    });
  };
};

const CircuitTicks = ({ ticks, tickTransform }: TicksRendererProps<ScaleBand<string>>) => {
  const currentVenueUuid = useCurrentVenueUuid();
  const circuits = useVenueCircuitsByUuid(currentVenueUuid);
  return ticks.map(({ value, index, to }) => {
    return (
      <g key={`visx-tick-${index}`} transform={tickTransform}>
        <defs>
          <ColorStripesPattern
            id={`circuit-${value}`}
            colors={circuits?.[value]?.colors.join(":").split(":") || []}
          />
        </defs>
        <CircuitTick x={to.x} y={to.y + 10} circuit={circuits?.[value]} />
      </g>
    );
  });
};

export default function AnalyseGraph({
  data: {
    data: dataValues,
    axisKeys,
    axisLabels,
    breakdownKeys,
    breakdownColors,
    maxTotal,
    breakdownStackOrder,
    breakdownOverlap,
    axisDataMap,
    showTargets,
  },
  metric,
  axis,
  breakdown,
  onColumnClick,
}: Props) {
  const marginTop = 10;
  const marginLeft = maxTotal > 100 ? 60 : 50;
  // oxlint-disable-next-line prefer-const
  let { parentRef, width, height } = useParentSize({
    debounceTime: 10,
  });
  const index = useMemo(
    () =>
      d3Index(
        dataValues,
        (d) => d.axis,
        (d) => d.breakdown || "",
      ),
    [dataValues],
  );
  let minColWidth = 60;
  if (axis === "route") {
    minColWidth = 30;
  }
  if (axis === "circuit") {
    minColWidth = 85;
  }
  width = Math.max(width, index.size * minColWidth);

  const xMax = width - marginLeft;
  const yMax = height - marginTop - 30;

  let { xScale, yScale, series } = useMemo(() => {
    return {
      keys: breakdownKeys,
      xScale: scaleBand().domain(axisKeys).paddingInner(0.5).paddingOuter(0.25),
      // yScale: scaleLinear({
      //   range: [yMax, 0],
      //   nice: true,
      //   domain: [0, maxTotal],
      // }),
      yScale: scaleLinear().domain([0, maxTotal]),
      series: d3Stack<[string | null, Map<string, (typeof dataValues)[number]>], string>()
        .keys(breakdownKeys)
        .order(breakdownStackOrder)
        .value(([, D], key) => D.get(key)?.value || 0)(index),
    };
  }, [axisKeys, breakdownKeys, maxTotal, index, breakdownStackOrder]);

  xScale = xScale.range([0, xMax]);
  yScale = yScale.range([yMax, 0]).nice();

  const [overId, setOverId] = useState<string | null>(null);

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<{
      datum: MapValue<GraphDataMap>;
      tooltipType: "default" | "targetWarning";
    }>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    // use TooltipWithBounds
    detectBounds: true,
    // when tooltip containers are scrolled, this will correctly update the Tooltip position
    scroll: true,
    zIndex: 1000,
  });

  const onMouseOver = (
    event: React.MouseEvent<SVGRectElement | SVGGElement, MouseEvent>,
    axisId: string,
    tooltipType: "default" | "targetWarning" = "default",
  ) => {
    const svg = (event.target as SVGElement).ownerSVGElement;
    const coords = svg ? localPoint(svg, event) : null;
    const datum = axisDataMap.get(axisId);
    if (!coords || !datum) {
      return;
    }
    setOverId(axisId || "");
    showTooltip({
      tooltipData: { datum, tooltipType },
      tooltipLeft: coords.x,
      tooltipTop: coords.y,
    });
  };

  const onBarClick = (axisId: string) => {
    const datum = axisDataMap.get(axisId);
    if (datum) {
      onColumnClick(Array.from(datum.uuids));
    }
  };

  let graphContent: React.ReactNode;
  const xScaleFull = xScale.copy().paddingInner(0).paddingOuter(0);

  const tooltipGroup = (
    <g>
      {Array.from(axisDataMap.values(), (datum) => {
        const isOver = overId === (datum.id || "");
        const axisId = datum.id || "";
        return (
          <g key={datum.id}>
            <rect
              x={xScaleFull(axisId)}
              y={0}
              width={xScaleFull.bandwidth()}
              height={yMax}
              fill="transparent"
              onMouseOver={(event) => onMouseOver(event, axisId)}
              onMouseMove={(event) => onMouseOver(event, axisId)}
              onClick={() => onBarClick(axisId)}
              onMouseOut={() => {
                hideTooltip();
                setOverId(null);
              }}
              strokeWidth={1}
              stroke={isOver ? "#2277C2" : "transparent"}
              rx={4}
            />
            {/* Target warning tooltip for axis label area */}
            {showTargets && datum.targetStatus && (
              <rect
                x={xScaleFull(axisId)}
                y={yMax}
                width={xScaleFull.bandwidth()}
                height={30}
                fill="transparent"
                onMouseOver={(event) => onMouseOver(event, axisId, "targetWarning")}
                onMouseMove={(event) => onMouseOver(event, axisId, "targetWarning")}
                onMouseOut={() => {
                  hideTooltip();
                  setOverId(null);
                }}
              />
            )}
          </g>
        );
      })}
    </g>
  );

  const bgRects = (
    <g>
      {axisKeys.map((axisId, i) => {
        const isOver = overId === axisId;
        return (
          <rect
            key={i}
            x={xScaleFull(axisId || "")}
            y={0}
            width={xScaleFull.bandwidth()}
            height={yMax}
            fill={isOver ? "#E5EFFF" : "black"}
            fillOpacity={isOver ? 1 : i % 2 === 0 ? 0 : 0.03}
          />
        );
      })}
    </g>
  );

  if (breakdown === null) {
    graphContent = dataValues.map((item) => {
      return (
        <rect
          key={(item.axis || "") + "-" + item.breakdown}
          x={xScale(item.axis || "")}
          y={yScale(item.value)}
          width={xScale.bandwidth()}
          height={yMax - yScale(item.value)}
          fill={"#2277C2"}
          // onMouseOver={(event) => onMouseOver(event, item.axis)}
        />
      );
    });
  } else if (breakdownOverlap) {
    graphContent = Array.from(index.entries(), ([axisId, breakdownMap]) => {
      return (
        <g key={axisId}>
          {Array.from(breakdownMap.values(), ({ axis, breakdown, value }) => {
            return (
              <rect
                key={axis + "-" + breakdown}
                x={xScale(axis || "")}
                y={yScale(value)}
                width={xScale.bandwidth()}
                height={yMax - yScale(value)}
                // breakdownColors comes from Object.fromEntries, so a null
                // breakdown is keyed as "null"
                fill={breakdownColors[String(breakdown)]}
                strokeWidth={1}
                strokeOpacity={0.15}
                stroke="#000"
              />
            );
          })}
        </g>
      );
    });
  } else {
    graphContent = series.map((item) => {
      return (
        <g
          key={item.key}
          fill={
            breakdownColors[item.key].includes(":")
              ? `url(#pattern-${item.key})`
              : breakdownColors[item.key]
          }
        >
          {item.map((d) => {
            if (d) {
              // debugger;
            }
            return (
              <>
                <rect
                  x={xScale(d.data[0] || "")}
                  y={yScale(d[1])}
                  width={xScale.bandwidth()}
                  height={yScale(d[0]) - yScale(d[1])}
                />
                <rect
                  x={(xScale(d.data[0] || "") ?? 0) + 1}
                  y={yScale(d[1]) + 1}
                  width={Math.max(xScale.bandwidth() - 2, 0)}
                  height={Math.max(yScale(d[0]) - yScale(d[1]) - 2, 0)}
                  strokeWidth={1}
                  strokeOpacity={0.15}
                  stroke="#000"
                />
              </>
            );
          })}
        </g>
      );
    });
  }

  let targetRects: React.ReactNode = null;
  if (showTargets) {
    targetRects = Array.from(axisDataMap.values(), (datum) => {
      if (datum.targetTotal === undefined) {
        return null;
      }
      return (
        <>
          <rect
            key={`target-stroke-${datum.id}`}
            x={xScale(datum.id || "")! - 8}
            y={yScale(datum.targetTotal) - 1.5}
            width={xScale.bandwidth() + 16}
            height={3}
            stroke="white"
            strokeOpacity={1}
            strokeWidth={3}
            rx={1.5}
          />
          <rect
            key={`target-${datum.id}`}
            x={xScale(datum.id || "")! - 8}
            y={yScale(datum.targetTotal) - 1.5}
            width={xScale.bandwidth() + 16}
            height={3}
            fill={
              datum.targetStatus === "danger"
                ? "#FD5348"
                : datum.targetStatus === "warning"
                  ? "#f9a825"
                  : "black"
            }
            rx={1.5}
          />
        </>
      );
    });
  }

  const GradeTicks = useMemo(
    () => getGradeTicksComponent(!!showTargets, axisDataMap),
    [showTargets, axisDataMap],
  );

  const ticksComponent =
    axis === "route"
      ? RouteTicks
      : axis === "circuit"
        ? CircuitTicks
        : axis === "grade"
          ? GradeTicks
          : undefined;

  return (
    <Box
      ref={parentRef}
      sx={{
        padding: 0,
        minHeight: 200,
        height: "100%",
        overflowX: "auto",
        overflowY: "hidden",
        position: "relative",
      }}
    >
      <svg ref={containerRef} width={width} height={height}>
        <defs>
          {Object.entries(breakdownColors).map(([key, c]) => {
            const colors = (c || "#F1F1F4").split(":");
            if (colors.length === 1) {
              return null;
            }
            return <ColorStripesPattern key={key} id={`pattern-${key}`} colors={colors} />;
          })}
        </defs>
        <text
          textAnchor="middle"
          transform={`translate(20, ${height / 2 - marginTop}) rotate(-90)`}
          fontSize={14}
        >
          {metrics[metric].label}
        </text>
        <GridRows
          top={marginTop}
          left={marginLeft}
          scale={yScale}
          width={xMax}
          height={yMax}
          stroke="#F1F1F4"
          strokeOpacity={1}
          strokeDasharray="4,2"
        />
        <Group top={marginTop} left={marginLeft}>
          {bgRects}
          {graphContent}
          {targetRects}
        </Group>
        <AxisLeft
          top={marginTop}
          left={marginLeft}
          scale={yScale}
          tickValues={yScale.ticks().filter((tick) => Number.isInteger(tick))}
          tickFormat={yScale.tickFormat(10, ",d")}
          tickLength={6}
          tickLabelProps={() => ({
            fontSize: 11,
            textAnchor: "end",
            verticalAnchor: "middle",
            fontWeight: "bold",
          })}
          stroke="#C5C9D3"
          tickStroke="transparent"
        />
        <AxisBottom
          top={yMax + marginTop}
          left={marginLeft}
          scale={xScale}
          numTicks={axisKeys.length}
          tickLabelProps={{
            fontSize: 11,
            textAnchor: "middle",
            fontWeight: "bold",
            fontFamily: "IBM Plex Sans",
          }}
          tickStroke="transparent"
          tickLength={4}
          tickFormat={(value) => axisLabels.get(value)}
          ticksComponent={ticksComponent}
          stroke="#C5C9D3"
        />
        <Group top={marginTop} left={marginLeft}>
          {tooltipGroup}
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          zIndex={1000}
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            zIndex: 1000,
            position: "absolute",
          }}
        >
          <TooltipContent
            {...{
              tooltipData: tooltipData.datum,
              tooltipType: tooltipData.tooltipType,
              axis,
              breakdown,
              breakdownColors,
            }}
          />
        </TooltipInPortal>
      )}
    </Box>
  );
}

const TargetStatusChip = ({
  targetCount,
  currentCount,
  targetStatus,
}: {
  targetCount: number;
  currentCount: number;
  targetStatus?: "warning" | "danger";
}) => {
  const diff = currentCount - targetCount;
  if (!targetStatus) {
    return null;
  }
  return (
    <Stack
      sx={{
        backgroundColor: `${targetStatus}.200`,
        color: `${targetStatus}.600`,
        fontSize: "14px",
        borderRadius: "200px",
        ".MuiSvgIcon-root": {
          marginRight: "-6px",
          marginLeft: "-4px",
          fontSize: "18px",
        },
        px: 0.75,
      }}
      direction="row"
      spacing={0.5}
      alignItems="center"
    >
      <span>
        {diff > 0 ? "+" : ""}
        {diff}
      </span>
    </Stack>
  );
};

const TooltipContent = ({
  tooltipData,
  tooltipType = "default",
  breakdown,
  axis,
  breakdownColors,
}: {
  tooltipData: MapValue<GraphDataMap>;
  tooltipType?: "default" | "targetWarning";
  breakdown: BreakdownId | null;
  axis: DimensionId;
  breakdownColors: Record<string, string>;
}) => {
  const routes = useVenueRoutesByUuid(useCurrentVenueUuid());
  const route: Route | undefined = routes?.[tooltipData.id || ""];

  const diff = tooltipData.total - (tooltipData.targetTotal || 0);
  const text = diff > 0 ? "Over target" : "Under target";

  if (tooltipType === "targetWarning") {
    const gradeValue = tooltipData.name;

    return (
      <Sheet variant="outlined" sx={{ borderRadius: 8, boxShadow: "lg" }}>
        <Stack gap={1} sx={{ padding: 1.5, minWidth: 180 }}>
          <Typography level="body-sm" fontWeight="bold" color={tooltipData.targetStatus}>
            {tooltipData.targetStatus === "warning" ? "⚠️ " : null}
            {tooltipData.targetStatus === "danger" ? "❗️ " : null}
            {text} for grade {gradeValue}
          </Typography>
          <Stack direction="row" justifyContent="space-between">
            <Typography level="body-sm" sx={{ display: "inline" }}>
              Target:
            </Typography>
            <Typography level="body-sm" fontWeight="bold">
              {tooltipData.targetTotal}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography level="body-sm" sx={{ display: "inline" }}>
              Difference:
            </Typography>
            <Box sx={{ marginRight: -0.5, marginTop: -0.25 }}>
              <TargetStatusChip
                targetCount={tooltipData.targetTotal || 0}
                currentCount={tooltipData.total}
                targetStatus={tooltipData.targetStatus}
              />
            </Box>
          </Stack>
        </Stack>
      </Sheet>
    );
  }

  return (
    <Sheet variant="outlined" sx={{ borderRadius: 8, boxShadow: "lg" }}>
      <Stack
        gap={0}
        sx={{
          paddingBottom: 0.5,
          ".label": {
            width: 150,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
          ".value": {
            textAlign: "right",
            width: 30,
          },
          ".chip": {
            width: 36,
            display: "flex",
            justifyContent: "flex-start",
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          gap={1}
          sx={{ padding: 0.75, paddingY: 1 }}
        >
          {axis === "route" ? (
            <Box sx={{ display: "flex", alignItems: "center" }} className="label">
              {route ? <RouteName route={route} /> : "?"}
            </Box>
          ) : (
            <Typography level="body-sm" fontWeight="bold" lineHeight={1} className="label">
              {axis === "grade" ? "Grade: " : null}
              {tooltipData.name}
            </Typography>
          )}
          <Typography level="body-sm" fontWeight="bold" lineHeight={1} className="value">
            {tooltipData.total.toString()}
          </Typography>
          {tooltipData.targetStatus && tooltipData.targetTotal !== undefined ? (
            <Box className="chip">
              <TargetStatusChip
                targetCount={tooltipData.targetTotal}
                currentCount={tooltipData.total}
                targetStatus={tooltipData.targetStatus}
              />
            </Box>
          ) : null}
        </Stack>
        {Array.from(tooltipData.values.values(), (breakdownItem) => {
          const breakdownDimension = getBreakdownDimension(breakdown);
          const displayAsPercentage =
            breakdownDimension &&
            "displayAsPercentage" in breakdownDimension &&
            breakdownDimension.displayAsPercentage;

          const formattedValue = displayAsPercentage
            ? formatNumber((breakdownItem.value / tooltipData.total) * 100, {
                maxDecimals: 0,
              }) + "%"
            : formatNumber(breakdownItem.value);
          return (
            <Stack
              key={breakdownItem.id}
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={1}
              sx={{ paddingX: 1, paddingY: 0.5 }}
              className="item"
            >
              <Stack direction="row" gap={0.5} alignItems="center" className="label">
                <RouteCircle
                  colors={[breakdownColors[breakdownItem.id || ""]]}
                  style={{ width: 18, height: 18 }}
                />
                <Typography
                  level="body-sm"
                  sx={{
                    maxWidth: 150,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  fontWeight="bold"
                >
                  {breakdownItem.name}
                </Typography>
              </Stack>
              <Typography level="body-sm" fontWeight="bold" className="value">
                {formattedValue}
              </Typography>
              {breakdownItem.targetStatus && breakdownItem.target !== undefined ? (
                <Box className="chip">
                  <TargetStatusChip
                    targetCount={breakdownItem.target}
                    currentCount={breakdownItem.value}
                    targetStatus={breakdownItem.targetStatus}
                  />
                </Box>
              ) : null}
            </Stack>
          );
        })}
      </Stack>
    </Sheet>
  );
};
